import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import misumiAmericasLogoUrl from '../assets/brand/misumi-americas-logo.png'
import { FormAlert } from '../components/auth/FormAlert'
import { FormField } from '../components/auth/FormField'
import { apiClient } from '../lib/api/client'
import { registerProblemCopy } from '../lib/auth/problem-messages'

const MIN_PASSWORD_LENGTH = 12
const MAX_PASSWORD_LENGTH = 200

/**
 * Self-serve credential registration (F0.3, PR-AUTH-01, `POST /auth/register`).
 *
 * UID-301 (design-fidelity pass): rendered in the same standalone split-panel
 * shell as `<LoginPage>` (centered form + brand hero, no app chrome), with the
 * primary button matching the login "Log in" button.
 *
 * SCOPE NOTE: this is registration of an email/password IDENTITY only — the
 * Gate 2 UX spec's "Create your company account" wizard is account/tenant
 * creation (onboarding, S0.8/PR-ONB-02/03, a separate story) and needs
 * `/onboarding/*` endpoints this identity has no session for yet. Flagged to
 * the EM/PM rather than invented.
 */
export function SignupPage() {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') ?? undefined

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName] = useState('')

  const [emailError, setEmailError] = useState<string | undefined>()
  const [passwordError, setPasswordError] = useState<string | undefined>()
  const [confirmError, setConfirmError] = useState<string | undefined>()
  const [serverError, setServerError] = useState<{ title: string; detail: string } | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [registered, setRegistered] = useState(false)

  const alertRef = useRef<HTMLDivElement | null>(null)
  const confirmationHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const passwordInputRef = useRef<HTMLInputElement | null>(null)
  const confirmInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (serverError) {
      alertRef.current?.focus()
    }
  }, [serverError])

  useEffect(() => {
    if (registered) {
      confirmationHeadingRef.current?.focus()
    }
  }, [registered])

  // USP-42: client-side validation errors must move focus and be announced,
  // focusing the first invalid field in form order.
  useEffect(() => {
    if (emailError) {
      emailInputRef.current?.focus()
    } else if (passwordError) {
      passwordInputRef.current?.focus()
    } else if (confirmError) {
      confirmInputRef.current?.focus()
    }
  }, [emailError, passwordError, confirmError])

  function loginHref(): string {
    return returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'
  }

  function validate(): boolean {
    let ok = true
    if (!email.trim()) {
      setEmailError('Enter your email address.')
      ok = false
    } else {
      setEmailError(undefined)
    }

    if (!password) {
      setPasswordError('Enter a password.')
      ok = false
    } else if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
      setPasswordError(
        `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`,
      )
      ok = false
    } else {
      setPasswordError(undefined)
    }

    if (confirmPassword !== password) {
      setConfirmError("Passwords don't match.")
      ok = false
    } else {
      setConfirmError(undefined)
    }

    return ok
  }

  async function handleSubmit() {
    if (!validate()) {
      return
    }
    setServerError(undefined)
    setSubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/auth/register', {
        body: {
          email: email.trim(),
          password,
          displayName: displayName.trim() || null,
        },
      })
      if (data) {
        setRegistered(true)
        return
      }
      const copy = registerProblemCopy(error?.type, error?.detail)
      setServerError(copy)
    } catch {
      setServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  // Standalone split-panel wrapper shared by both states — mirrors <LoginPage>.
  function withBrandPanel(content: ReactNode) {
    return (
      <div className="login-split">
        <div className="login-split-form-col">
          <section className="auth-page login-form-panel">
            <img src={misumiAmericasLogoUrl} alt="MISUMI AMERICAS" className="login-brand-mark" />
            {content}
          </section>
        </div>
        <div className="login-brand-panel" aria-hidden="true" />
      </div>
    )
  }

  if (registered) {
    return withBrandPanel(
      <>
        <h1 className="display-heading login-heading" ref={confirmationHeadingRef} tabIndex={-1}>
          Check your email
        </h1>
        {/* Neutral wording (C19, no user enumeration): identical whether or not
            the address was already registered. */}
        <p className="login-subtitle">
          If <strong>{email.trim()}</strong> can be registered, we've sent a verification link to
          it. Follow the link to finish setting up your account.
        </p>
        <div className="auth-links login-secondary-links">
          <Link to={loginHref()}>Back to sign in</Link>
        </div>
      </>,
    )
  }

  return withBrandPanel(
    <>
      <h1 className="display-heading login-heading">Create your account</h1>
      <p className="login-subtitle">
        Already have an account? <Link to={loginHref()}>Sign in</Link>
      </p>
      {serverError && (
        <FormAlert ref={alertRef} title={serverError.title} detail={serverError.detail} />
      )}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleSubmit()
        }}
      >
        <FormField
          ref={emailInputRef}
          id="signup-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          error={emailError}
          required
        />
        <FormField
          ref={passwordInputRef}
          id="signup-password"
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          error={passwordError}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={MAX_PASSWORD_LENGTH}
          required
        />
        <FormField
          ref={confirmInputRef}
          id="signup-confirm-password"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={confirmError}
          required
        />
        <FormField
          id="signup-display-name"
          label="Display name (optional)"
          type="text"
          autoComplete="name"
          value={displayName}
          onChange={setDisplayName}
        />
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-lg login-submit login-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </div>
      </form>
    </>,
  )
}
